import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * TCN Icon — minimal hand-drawn line icon set, currentColor stroke.
 * Single SVG component, tree-shake friendly via the `name` prop.
 *
 * Usage: <Icon name="home" size={20} />
 *
 * Available names (CLAUDE_TCN.md §7 spec):
 *   home, people, tiers, cash, check, bell, search, filter,
 *   arrow-right, arrow-up, arrow-down, plus, close,
 *   chevron, chevron-down, flame, diamond, sparkle, phone,
 *   calendar, clock, warn, check-circle, edit, more, translate,
 *   link, refresh, pin, fire, target, spark
 *
 * RTL: directional icons (arrow-right, chevron, chevron-left) auto-flip
 * via scaleX(-1) when document direction is RTL (R5 — arabic).
 */
const DIRECTIONAL_ICONS = new Set(['arrow-right', 'chevron', 'chevron-left']);

const PATHS = {
  home: (
    <>
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10h4v-6h6v6h4V10" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c.6-3.4 3.4-5.5 6.5-5.5s5.9 2.1 6.5 5.5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M15.5 14.5c2.7 0 5 1.7 5.5 4.5" />
    </>
  ),
  tiers: (
    <>
      <path d="M3 14l9-5 9 5" />
      <path d="M3 18l9-5 9 5" />
      <path d="M3 10l9-5 9 5" />
    </>
  ),
  cash: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 9.5v.01M18 14.5v.01" />
    </>
  ),
  check: <path d="M5 12l4.5 4.5L19 7" />,
  bell: (
    <>
      <path d="M6 16V11a6 6 0 1 1 12 0v5" />
      <path d="M5 16h14l-1 2H6z" />
      <path d="M10.5 21a1.5 1.5 0 0 0 3 0" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </>
  ),
  filter: <path d="M4 5h16l-6 8v6l-4-2v-4z" />,
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  'arrow-right': (
    <>
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </>
  ),
  'arrow-up': (
    <>
      <path d="M12 19V5" />
      <path d="M6 11l6-6 6 6" />
    </>
  ),
  'arrow-down': (
    <>
      <path d="M12 5v14" />
      <path d="M6 13l6 6 6-6" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </>
  ),
  chevron: <path d="M9 6l6 6-6 6" />,
  'chevron-left': <path d="M15 6l-6 6 6 6" />,
  'chevron-down': <path d="M6 9l6 6 6-6" />,
  flame: (
    <>
      <path d="M12 3s4 4 4 8a4 4 0 0 1-8 0c0-2 1-3 1-4 0 2 1 3 3 4z" />
    </>
  ),
  diamond: (
    <>
      <path d="M6 9l3-4h6l3 4-6 12z" />
      <path d="M6 9h12" />
      <path d="M9 5l3 4 3-4" />
    </>
  ),
  sparkle: (
    <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
  ),
  phone: (
    <>
      <path d="M5 4h4l2 5-3 2a11 11 0 0 0 5 5l2-3 5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 9h16M9 3v4M15 3v4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  warn: (
    <>
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v5" />
      <path d="M12 18.01v.01" />
    </>
  ),
  'check-circle': (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8 12.5l3 3 5-6" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4L19 9l-4-4L4 16z" />
      <path d="M14 6l4 4" />
    </>
  ),
  more: (
    <>
      <circle cx="6" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="18" cy="12" r="1.4" fill="currentColor" />
    </>
  ),
  translate: (
    <>
      <path d="M4 5h7M7.5 5v2c0 4-3.5 7-3.5 7" />
      <path d="M5 11s1.5 3 4.5 3" />
      <path d="M12 20l5-12 5 12" />
      <path d="M14 16h6" />
    </>
  ),
  link: (
    <>
      <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.5 1.5" />
      <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.5-1.5" />
    </>
  ),
  refresh: (
    <>
      <path d="M21 12a9 9 0 1 1-3.3-7" />
      <path d="M21 4v5h-5" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21l-1-6-4-1 4-3-1-6 4 4 6-1-3 4 4 6-6-1z" />
    </>
  ),
  fire: (
    <>
      <path d="M12 3c1 3 5 5 5 9a5 5 0 0 1-10 0c0-2 1-3 2-4 0 2 1 3 3 4-1-3 0-6 0-9z" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </>
  ),
  spark: (
    <path d="M3 17l5-5 4 3 9-9" />
  ),
};

const Icon = ({
  name,
  size = 20,
  strokeWidth = 1.8,
  className = '',
  style,
  color,
  ...rest
}) => {
  const { i18n } = useTranslation();
  const path = PATHS[name];
  if (!path) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(`[TCN Icon] unknown name "${name}"`);
    }
    return null;
  }

  const flip = DIRECTIONAL_ICONS.has(name) && i18n.dir() === 'rtl';
  const mergedStyle = flip
    ? { ...style, transform: `scaleX(-1)${style?.transform ? ` ${style.transform}` : ''}` }
    : style;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? 'currentColor'}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      style={mergedStyle}
      {...rest}
    >
      {path}
    </svg>
  );
};

export default Icon;
